/**
 * OIMS v2 - Main entry point
 * Imports all modules, orchestrates init order, binds global events
 */

import { initTheme, toggleTheme } from './theme.js';
import { initSidebar, switchTabByIndex } from './sidebar.js';
import { initUtils, showLoading, hideLoading } from './utils.js';
import { initAuth, parseTokenFromHash, loadToken, logSessionIfNeeded, updateAuthUI, logout } from './auth.js';
import { initActions, loadActions, loadInstances, confirmAndExecute } from './actions.js';
import { initLogs } from './logs.js';
import { initBackup } from './backup.js';
import { initWindows, loadWindowsInstances } from './windows.js';
import { initStatus, loadHealthGroups, stopHealthAutoRefresh } from './status.js';
import { initCharts } from './charts.js';
import { initNotifications, cleanupNotifications, registerServiceWorker } from './notifications.js';
import { initDashboard, loadDashboard } from './dashboard.js';
import { initApprovals, cleanupApprovals } from './approvals.js';
import { initProfile, updateProfileInfo } from './profile.js';
import { initAws, loadAwsData } from './aws.js';

// Global error boundary
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  if (window.toast) {
    const msg = event.reason?.message || "An unexpected error occurred";
    window.toast.error("Unhandled error: " + msg);
  }
});

// Shared state object
const state = {
  accessToken: null,
  instanceNames: {},
};

// --- Init sequence ---

// 1. Theme (immediate - prevent flash)
initTheme();

// Run everything else after DOM is ready
document.addEventListener("DOMContentLoaded", async () => {
  // 2. Sidebar navigation
  initSidebar();

  // 3. Cache DOM refs for utils
  initUtils();

  // 4. Charts defaults
  initCharts(state);

  // 5. Auth setup (axios interceptors, session guard)
  initAuth(state);

  // 6. Init tab modules (wire event listeners)
  initDashboard(state);
  initActions(state);
  initLogs(state);
  initBackup(state);
  initWindows(state);
  initStatus(state);
  initAws(state);

  // 6b. Profile panel
  initProfile(state);

  // 7. Bind global events
  document.getElementById("btnThemeToggle")?.addEventListener("click", toggleTheme);
  document.getElementById("btnLogout")?.addEventListener("click", () => {
    cleanupNotifications();
    cleanupApprovals();
    logout(state, false);
  });

  // Keyboard shortcuts: Ctrl+1-5 = switch tabs, Ctrl+Enter = execute
  document.addEventListener("keydown", (e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;

    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      confirmAndExecute(state);
    } else if (e.ctrlKey && e.key >= "1" && e.key <= "6") {
      e.preventDefault();
      switchTabByIndex(parseInt(e.key));
    }
  });

  // 8. Load data
  showLoading("Initializing...");
  try {
    await parseTokenFromHash(state);
    loadToken(state);
    await logSessionIfNeeded(state);
    updateAuthUI(state);
    updateProfileInfo(state);

    if (state.accessToken) {
      showLoading("Loading configuration...");
      await loadPermissions(state);
      await loadActions(state);
      await loadInstanceNames(state);
      const initialAction = document.getElementById("selAction")?.value || null;
      loadInstances(state, initialAction);

      // Load Windows instances if the tab exists
      const sidebarWindows = document.querySelector('[data-tab-target="windows"]');
      if (sidebarWindows) {
        await loadWindowsInstances(state);
      }

      await loadHealthGroups(state);

      // Load AWS tab data if tab exists
      const sidebarAws = document.querySelector('[data-tab-target="aws"]');
      if (sidebarAws) {
        await loadAwsData();
      }

      // Load dashboard (default landing tab)
      await loadDashboard();

      // Register service worker for push notifications
      await registerServiceWorker();

      // Start notification system
      initNotifications(state);

      // Start approval system (Maker/Checker)
      initApprovals(state);
    }
  } catch (e) {
    console.error("Initialization error:", e);
    if (window.toast) window.toast.error("Failed to initialize application");
  } finally {
    hideLoading();
  }
});

// --- Permission loading (uses sidebar API) ---

import { removeSidebarTab, activateFirstTab } from './sidebar.js';

async function loadPermissions(state) {
  if (!state.accessToken) return;
  try {
    const { data } = await axios.get("/auth/permissions");
    const allowedTabs = data.tabs || [];

    const tabMap = {
      "actions": "actions",
      "logs": "logs",
      "backup": "backup",
      "windows": "windows",
      "status": "status",
      "aws": "aws"
    };

    const removedTabs = [];

    for (const [tabId, permKey] of Object.entries(tabMap)) {
      if (!allowedTabs.includes(permKey)) {
        // Remove sidebar link and tab pane
        removeSidebarTab(tabId);
        const tabPane = document.getElementById(tabId);
        if (tabPane) tabPane.remove();
        removedTabs.push(permKey);
      }
    }

    if (removedTabs.length > 0) {
      activateFirstTab();
    }

    // Hide AWS sub-tabs based on action permissions
    const allowedActions = data.actions || [];
    const awsSubTabMap = {
      "allow_ip": { tab: "aws-whitelist-tab", pane: "aws-whitelist-pane" },
      "schedule_instance": { tab: "aws-scheduling-tab", pane: "aws-scheduling-pane" },
    };
    let visibleSubTabs = 0;
    for (const [action, ids] of Object.entries(awsSubTabMap)) {
      if (!allowedActions.includes(action)) {
        document.getElementById(ids.tab)?.closest('li')?.remove();
        document.getElementById(ids.pane)?.remove();
      } else {
        visibleSubTabs++;
      }
    }
    // Activate first visible sub-tab if the active one was removed
    if (visibleSubTabs > 0) {
      const activeSubTab = document.querySelector('#awsSubTabs .nav-link.active');
      if (!activeSubTab) {
        const firstTab = document.querySelector('#awsSubTabs .nav-link');
        if (firstTab) {
          firstTab.classList.add('active');
          firstTab.setAttribute('aria-selected', 'true');
          const targetPane = document.querySelector(firstTab.dataset.bsTarget);
          if (targetPane) {
            targetPane.classList.add('show', 'active');
          }
        }
      }
    }
  } catch (e) {
    console.error("loadPermissions error", e);
  }
}

// --- Instance name mapping ---

async function loadInstanceNames(state) {
  if (!state.accessToken) return;
  try {
    const { data } = await axios.get("/actions/instance-names");
    state.instanceNames = data.instance_names || {};
  } catch (e) {
    console.error("loadInstanceNames error", e);
  }
}

// Add CSS for spinning icon
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
    display: inline-block;
  }
`;
document.head.appendChild(style);
